import { BalanceTypeDescription } from 'types/Balances';

export type BalanceAggregate = { key: string; total: number | string };

// Privacidade do split no extrato do lojista: o split (enviado/recebido) NÃO
// deve aparecer no totalizador — antes do 55bc4b8 os cards só listavam
// Venda/Taxa/Saque. A separação received/sent segue intacta nas linhas e no
// admin; aqui só excluímos o split dos TOTAIS. Cobre SPLIT/SPLIT_RECEIVED/
// SPLIT_SENT e CUSTOMER_SPLIT/CUSTOMER_SPLIT_RECEIVED/CUSTOMER_SPLIT_SENT, com
// ou sem sufixo de origem (_SELL, _RECHARGE, ...).
export function isSplitAggregateKey(key: string): boolean {
  return key.startsWith('SPLIT') || key.startsWith('CUSTOMER_SPLIT');
}

// Keys from /balances/totals-by-origin come as `${type}_${origin}` (e.g.
// FEE_SELL, DEPOSIT_RECHARGE, CARD_RECHARGE_CARD_RECHARGE). Longest suffix
// first so CARD_RECHARGE is matched before RECHARGE.
const ORIGIN_SUFFIXES: { suffix: string; origin: string }[] = [
  { suffix: '_CARD_RECHARGE', origin: 'CARD_RECHARGE' },
  { suffix: '_RECHARGE', origin: 'RECHARGE' },
  { suffix: '_WITHDRAW', origin: 'WITHDRAW' },
  { suffix: '_SELL', origin: 'SELL' },
];

export const ORIGIN_LABEL: Record<string, string> = {
  SELL: 'Vendas',
  RECHARGE: 'Recargas',
  CARD_RECHARGE: 'Recarga de cartão',
  WITHDRAW: 'Saques',
};

const ORIGIN_ORDER = ['SELL', 'RECHARGE', 'CARD_RECHARGE', 'WITHDRAW'];

// Within a section the principal value comes first, then fees/splits/etc.
const TYPE_ORDER = [
  'MOVEMENT',
  'MOVEMENT_MAIN',
  'WITHDRAWAL',
  'DEPOSIT',
  'CARD_RECHARGE',
  'ACCOUNT_RECHARGE',
  'SYSTEM_FEE',
  'FEE',
  'ACCOUNT_LIMIT',
  'SPLIT_RECEIVED',
  'SPLIT_SENT',
  'CUSTOMER_SPLIT_RECEIVED',
  'CUSTOMER_SPLIT_SENT',
  'COMMISSION',
  'COMMISSION_RECEIVED',
  'COMMISSION_PAID',
  'SPREAD',
  'TRANSFER',
  'TRANSFER_RECEIVED',
  'TRANSFER_SENT',
  'LOCK',
  'INSURANCE',
  'ANTECIPATION',
  'WITHDRAWAL_ANTECIPATION',
];

function isKnownType(base: string): boolean {
  return Object.prototype.hasOwnProperty.call(BalanceTypeDescription, base);
}

// Split a key into base type + origin, validating the base against the label
// registry so types that themselves end in a suffix word (ACCOUNT_RECHARGE,
// CARD_RECHARGE) are not mis-parsed.
export function splitOriginKey(key: string): { base: string; origin: string | null } {
  if (isKnownType(key)) return { base: key, origin: null };
  for (const { suffix, origin } of ORIGIN_SUFFIXES) {
    if (key.endsWith(suffix)) {
      const base = key.slice(0, -suffix.length);
      if (isKnownType(base)) return { base, origin };
    }
  }
  return { base: key, origin: null };
}

export type AggregateSection = {
  origin: string | null;
  label: string;
  items: { key: string; label: string; total: number }[];
};

function originRank(origin: string | null): number {
  if (origin == null) return ORIGIN_ORDER.length + 1;
  const i = ORIGIN_ORDER.indexOf(origin);
  return i === -1 ? ORIGIN_ORDER.length : i;
}

function typeRank(base: string): number {
  const i = TYPE_ORDER.indexOf(base);
  return i === -1 ? 999 : i;
}

// Group totals into origin sections (Vendas / Recargas / Recarga de cartão /
// Saques / Outros), dropping zeros, with the types as ordered sub-items.
export function groupBalanceAggregatesByOrigin(items: BalanceAggregate[]): AggregateSection[] {
  const byOrigin = new Map<string, AggregateSection>();

  for (const item of items) {
    const total = Number(item.total);
    if (!(Math.abs(total) > 0)) continue;

    const { base, origin } = splitOriginKey(item.key);
    const groupKey = origin ?? '__OTHER__';

    let section = byOrigin.get(groupKey);
    if (!section) {
      section = {
        origin,
        label: origin ? ORIGIN_LABEL[origin] ?? origin : 'Outros',
        items: [],
      };
      byOrigin.set(groupKey, section);
    }

    section.items.push({ key: item.key, label: BalanceTypeDescription[base] ?? base, total });
  }

  const sections = [...byOrigin.values()];
  sections.sort((a, b) => originRank(a.origin) - originRank(b.origin));
  for (const section of sections) {
    section.items.sort((x, y) => {
      const rank = typeRank(splitOriginKey(x.key).base) - typeRank(splitOriginKey(y.key).base);
      return rank !== 0 ? rank : x.label.localeCompare(y.label, 'pt-BR');
    });
  }
  return sections;
}
